import { fetchNearestStores, getServicability } from '@api/store';
import { getStoreCategories } from '@api/storecategory';
import { TCategory, TGeoLocation, TServiceLocation, TStore } from '@components/types';
import { useInView } from 'framer-motion';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';

import { Container } from '@components';
import { StoreListCard } from '@components/stores/storecard';
import Location from '@components/location';
import { LocationNotice, StoreCategoryRail, StoreGridSkeleton, StoresEmpty, StoresHero, useSavedLocationName } from '@components/stores/parts';
import StoreFilters from '@components/widget/store-filters';
import StoreHorizontalController from '@components/widget/store-horizontal-controller';
import StoreSorting from '@components/widget/store-sorting';
import appConfig from '@core/appconfig';
import { getLocation } from '@core/geolocation';
import { geoLocationState } from '@recoil/atoms/address';
import { notification } from 'antd';
import Head from 'next/head';
import { useRecoilState } from 'recoil';

const Header = dynamic(
  () => import('../../src/components/header').then((mod) => mod.Header),
  {
    ssr: false,
  }
);

const Navbar = dynamic(
  () => import('../../src/components/navbar').then((mod) => mod.Navbar),
  {
    ssr: false,
  }
);

const Footer = dynamic(
  () => import('../../src/components/footer').then((mod) => mod.Footer),
  {
    ssr: false,
  }
);

const SORTING_OPTIONS = [
  { value: 'relevence', label: 'Relevence' },
  { value: 'popularity', label: 'Popularity' },
  { value: 'rating', label: 'Rating' },
];

const SERVICE_OPTIONS = [
  { value: 'isDelivery', label: 'Delivery' },
  { value: 'isPickup', label: 'Pickup' },
];

export default function StoresNearYou() {
  const [stores, setStores] = useState<TStore[]>([]);
  const [skip, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isData, setIsData] = useState(true);
  const [locationPopupShow, setLocationPopupShow] = useState(false);
  const PAGE_LIMIT = 10;
  const [categoryId, setCategoryId] = useState<null | number>(null);
  const [isDelivery, setIsDelivery] = useState(false);
  const [isPickup, setIsPickup] = useState(false);
  const [deliverableStores, setDeliverableStores] = useState([]);
  const [servicabilityChecked, setServicabilityChecked] = useState(false);
  const [geoposition, setGeoposition] = useRecoilState(geoLocationState);
  const [orderingOn, setOrderingOn] = useState<null | string>(
    SORTING_OPTIONS[0].value
  );

  const [allCategories, setAllCategories] = useState<TCategory[]>([]);
  const [minimumCategories, setMinimumCategories] = useState<TCategory[]>([]);

  const ref = useRef(null);
  const isInView = useInView(ref);

  const [api, contextHolder] = notification.useNotification();

  function handleCategoryChange(id: number) {
    if (categoryId === id) setCategoryId(null);
    else setCategoryId(id);
    setStores([]);
    setIsData(true);
    setSkip(0);
  }

  function handleServiceChange(service: 'isDelivery' | 'isPickup') {
    if (service === 'isDelivery') setIsDelivery(!isDelivery);
    else setIsPickup(!isPickup);
    setStores([]);
    setIsData(true);
    setSkip(0);
  }

  function handleSortingChange(option: string) {
    if (orderingOn === option) setOrderingOn(null);
    else setOrderingOn(option);
    setStores([]);
    setIsData(true);
    setSkip(0);
  }

  function handleClearFilters() {
    setCategoryId(null);
    setIsDelivery(false);
    setIsPickup(false);
    setOrderingOn(null);
    setStores([]);
    setIsData(true);
    setSkip(0);
  }

  async function fetchStores() {
    setIsLoading(true);
    try {
      const data = await fetchNearestStores(
        isDelivery,
        isPickup,
        categoryId,
        orderingOn,
        skip,
        PAGE_LIMIT
      );
      if (skip === 0) {
        setStores(data);
      } else {
        if (data.length) setStores([...stores, ...data]);
        else setIsData(false);
      }
    } catch (error) {
      setIsData(false);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  }

  async function fetchCategories() {
    const data: TCategory[] = await getStoreCategories();
    setAllCategories(data);
    setMinimumCategories(data.slice(0, 10));
  }

  useEffect(() => {
    if (stores?.length > 0) {
        const userLocation = JSON.parse(localStorage.getItem("location"));
        getServicability(stores?.map((s: TStore) => s.id), userLocation?.Location?.city_id, 0, stores.length).then((res) => {
            let deliveryStores = res?.map((item: TServiceLocation) => item?.store_id)
            setDeliverableStores(deliveryStores)
            setServicabilityChecked(true)
        })
        
    }
    }, [stores])

  useEffect(() => {
    if (!(geoposition?.latitude > 0 && geoposition?.longitude > 0)) {
      getLocation(true)?.then((location: TGeoLocation) => {
        setGeoposition({ last_updated: 0, longitude: location?.longitude, latitude: location?.latitude, code: location?.code || 0 });
      })
    }
  }, [])

  useEffect(() => {
    fetchStores();
  }, [isDelivery, isPickup, categoryId, orderingOn, skip, geoposition]);

  useEffect(() => {
    // The sentinel is visible while the first page is still loading; only page on once there is a list.
    if (isInView && isData && isLoading === false && hasLoaded && stores.length > 0) {
      setSkip((prevSkip) => prevSkip + PAGE_LIMIT);
      setIsLoading(true);
    }
  }, [isInView, isData, isLoading, hasLoaded]);


  useEffect(() => {
    fetchCategories();
  }, []);

  const locationName = useSavedLocationName(locationPopupShow);
  const locationUnavailable = geoposition?.code === 1 || !(geoposition?.latitude > 0 && geoposition?.longitude > 0);

  function retryLocation() {
    localStorage.removeItem('geoposition');
    getLocation(true)?.then((location: TGeoLocation) => {
      setGeoposition({ last_updated: 0, longitude: location?.longitude, latitude: location?.latitude, code: location?.code || 0 });
    });
  }

  return (
    <Container>
      <Head>
        <title>Stores Near You - {appConfig.appName}</title>
        <meta
          name="Keywords"
          content="Local Stores,Local+Online Stores,Stores near you,Local Services,Online Shopping,Jhattse"
        />
        <meta
          name="Description"
          content={`Find Stores and services near me whether local or online. ${appConfig.appName} provides product visiblity, best price discovering and all available deals and offers.`}
        />
        <link rel="canonical" href={`https://jhattse.com/store`} />
        <meta property="og:title" content={`Stores Near You - ${appConfig?.appName}`} />
        <meta
          property="og:description"
          content={`Find Stores and services around you whether local or online. ${appConfig.appName} provides product visiblity, best price discovering and all available deals and offers.`}
        />
        <meta property="og:url" content={`https://jhattse.com/store`} />
      </Head>

      <Header />

      <div className="flex flex-col font-manrope gap-4 w-full mx-auto max-w-[1240px] pt-4 pb-16">
        <StoresHero
          title="Stores near you"
          subtitle="Kirana, restaurants, pharmacies and more from local shops around you. Order for delivery or pick up."
          crumbs={[{ label: 'Home', href: '/' }, { label: 'Stores' }]}
        />
        <Location showModal={locationPopupShow} setShowModal={setLocationPopupShow} handleCallback={() => { setStores([]); setSkip(0); setIsData(true); }} />

        <StoreCategoryRail categories={allCategories} />

        {locationUnavailable && hasLoaded && <LocationNotice onRetry={retryLocation} onPick={() => setLocationPopupShow(true)} />}

        <div className="w-full h-fit grid grid-cols-1 md:grid-cols-4">
          <StoreFilters
            categoryId={categoryId}
            handleCategoryChange={handleCategoryChange}
            handleClearFilters={handleClearFilters}
            servicesFilter={true}
            isDelivery={isDelivery}
            isPickup={isPickup}
            handleServiceChange={handleServiceChange}
            allCategories={allCategories}
            minimumCategories={minimumCategories}
          />

          <div className="md:col-span-3">
            <StoreSorting
              options={SORTING_OPTIONS}
              orderingOn={orderingOn}
              handleSortingChange={handleSortingChange}
            />

            {/* to control sorting and filtering on smaller devices */}
            <StoreHorizontalController
              serviceOptions={SERVICE_OPTIONS}
              sortingOptions={SORTING_OPTIONS}
              handleServiceChange={handleServiceChange}
              handleSortingChange={handleSortingChange}
              categoryId={categoryId}
              categories={allCategories}
              handleCategoryChange={handleCategoryChange}
            />

            {!hasLoaded && stores.length === 0 ? (
              <StoreGridSkeleton />
            ) : hasLoaded && !isLoading && stores.length === 0 ? (
              <StoresEmpty
                title="No stores match these filters"
                hint="Try removing a filter or choosing a different category."
              />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 p-2">
                {stores?.map((store, i) => (
                  // Delivery status only means something once we know the visitor's area.
                  <StoreListCard key={store.id} store={store} priority={i < 4}
                    isAvailable={servicabilityChecked && locationName ? deliverableStores.includes(store.id) : undefined} />
                ))}
              </div>
            )}
            {isLoading && stores.length > 0 && <StoreGridSkeleton count={2} />}
            <div ref={ref} aria-hidden="true" className="h-1" />
          </div>
        </div>
      </div>
      <Navbar />
      <Footer />
    </Container>
  );
}
